/**
 * Prerender.io helpers.
 *
 * This deployment has NO server-side rendering: nginx serves the same static
 * `index.html` for every route, so the app cannot answer a request with an HTTP
 * status of its own. Crawlers, however, reach the site through Prerender.io,
 * which renders the page in a headless browser and turns the meta tags below
 * into the REAL HTTP response the crawler receives. That makes this the only
 * place in the stack that can emit a genuine 301 for a URL whose correct target
 * is only knowable after the article data has loaded.
 *
 * The mechanism is already proven in production by `PageNotFoundComponent`,
 * whose `prerender-status-code` tag makes a bad URL answer with a real 404.
 * See https://docs.prerender.io/docs/11-best-practices
 */

/** Token Prerender.io's renderer carries in its user agent — but only when the
 * account is NOT configured to forward the original crawler's user agent, which
 * this deployment is (verified on staging: a Googlebot request renders with
 * Googlebot's own UA). Hence `isPrerenderAgent` is best-effort only. */
export const PRERENDER_UA_TOKEN = "Prerender";

const STATUS_META_NAME = "prerender-status-code";
const HEADER_META_NAME = "prerender-header";

/**
 * BEST-EFFORT guess that the page is being rendered by Prerender.io rather than
 * shown to a person. It CANNOT be relied on — this deployment forwards the
 * crawler's user agent, so the renderer is indistinguishable from Googlebot —
 * which is exactly why `emitPrerenderRedirect` is called unconditionally and
 * this check only guards the one action that would DESTROY the emitted tags: a
 * hard navigation. When it guesses wrong the crawler gets a 200 carrying the
 * right content instead of a 301, never a wrong page.
 */
export function isPrerenderAgent(): boolean {
  if (typeof navigator === "undefined") {
    return false;
  }
  if (
    navigator.userAgent &&
    navigator.userAgent.indexOf(PRERENDER_UA_TOKEN) !== -1
  ) {
    return true;
  }
  // Headless Chrome driven over CDP (what Prerender.io runs) reports this.
  return (navigator as Navigator & { webdriver?: boolean }).webdriver === true;
}

/**
 * Emit `301 Moved Permanently` + `Location: <targetUrl>` for Prerender.io.
 * Idempotent: re-emitting replaces the previous pair rather than stacking tags,
 * since only the first of a repeated meta name is honored.
 */
export function emitPrerenderRedirect(doc: Document, targetUrl: string): void {
  if (!doc || !targetUrl) {
    return;
  }
  setPrerenderMeta(doc, STATUS_META_NAME, "301");
  setPrerenderMeta(doc, HEADER_META_NAME, `Location: ${targetUrl}`);
}

/**
 * Drop any previously emitted redirect tags. Needed because the tags are now
 * emitted unconditionally: a visitor who lands on a stale URL and then navigates
 * in-app to a canonical article would otherwise keep carrying the old page's
 * `301 + Location` in the document head.
 */
export function clearPrerenderRedirect(doc: Document): void {
  if (!doc) {
    return;
  }
  [STATUS_META_NAME, HEADER_META_NAME].forEach(name => {
    const meta = doc.head.querySelector(`meta[name='${name}']`);
    if (meta && meta.parentNode) {
      meta.parentNode.removeChild(meta);
    }
  });
}

/**
 * Whether the head already carries a 301 whose `Location` is `targetUrl`. A page
 * that finds itself on its canonical URL uses it to tell "a startup step just
 * redirected the crawler here" (keep the 301) from "a previous page left it"
 * (clear it). `normalizeLangUrl` is that startup step for a double-`lang` URL
 * such as `/news/<slug>/<id>/lang/products/fibra-analytics/lang/en`: it 301s to
 * `/news/<slug>/<id>/lang/en` and corrects the address bar before the article
 * loads, so the article sees an already-canonical path.
 *
 * Compared decoded, without a trailing slash and case-insensitively, because
 * `UrlCanonicalService` emits the target percent-encoded while article callers
 * hold it raw.
 */
export function hasPrerenderRedirectTo(
  doc: Document,
  targetUrl: string
): boolean {
  if (!doc || !targetUrl) {
    return false;
  }
  const status = doc.head.querySelector(`meta[name='${STATUS_META_NAME}']`);
  const header = doc.head.querySelector(`meta[name='${HEADER_META_NAME}']`);
  if (!status || status.getAttribute("content") !== "301" || !header) {
    return false;
  }
  const location = (header.getAttribute("content") || "").replace(
    /^Location:\s*/,
    ""
  );
  return (
    !!location &&
    normalizeRedirectUrl(location) === normalizeRedirectUrl(targetUrl)
  );
}

function normalizeRedirectUrl(url: string): string {
  let decoded = url;
  try {
    decoded = decodeURI(url);
  } catch (e) {}
  return decoded.replace(/\/+$/, "").toLowerCase();
}

function setPrerenderMeta(doc: Document, name: string, content: string): void {
  const existing = doc.head.querySelector(`meta[name='${name}']`);
  if (existing) {
    existing.setAttribute("content", content);
    return;
  }
  const meta = doc.createElement("meta");
  meta.setAttribute("name", name);
  meta.setAttribute("content", content);
  doc.head.appendChild(meta);
}
